<template>
  <section>
    <activity-log-summary
      :stats="stats"
      :filters="filters"
      @apply-view="applyView"
    />

    <activity-log-filter
      :value="filters"
      :options="options"
      :facets="facets"
      :locked-fields="lockedFields"
      :saved-views="savedViews"
      @update:filter="onFilterChange"
      @apply-view="applyView"
      @reset="resetFilters"
    />

    <b-card no-body>
      <b-card-body class="pb-0 d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <h4 class="mb-0">{{ title }}</h4>
          <small class="text-muted">
            <!--
              An exact count, not an estimate. On an audit screen "about 2,000 entries" invites the
              question of which ones are missing.
            -->
            {{ formatNumber(totalRecords) }} {{ totalRecords === 1 ? 'entry' : 'entries' }}
            <span v-if="activeFilterSummary"> · {{ activeFilterSummary }}</span>
          </small>
        </div>

        <div>
          <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            class="mr-1 d-inline-block w-auto"
          />
          <b-button size="sm" variant="outline-secondary" class="mr-1" @click="fetchAll">
            <feather-icon icon="RefreshCwIcon" :class="{ 'spin': loading }" />
          </b-button>
          <b-button size="sm" variant="primary" @click="onExport">
            <feather-icon icon="DownloadIcon" class="mr-50" />
            Export
          </b-button>
        </div>
      </b-card-body>

      <b-table
        :items="entries"
        :fields="tableFields"
        :busy="loading"
        responsive
        hover
        show-empty
        empty-text="No activity matches these filters"
        class="mb-0 activity-table"
        @row-clicked="openDetail"
      >
        <template #table-busy>
          <div class="text-center py-3">
            <b-spinner small /> <span class="ml-50">Loading activity…</span>
          </div>
        </template>

        <!-- Severity as a left edge rather than a column: colour without spending width. -->
        <template #cell(severity)="data">
          <div
            v-b-tooltip.hover
            class="severity-pip"
            :class="`bg-${severityColor(data.item.severity)}`"
            :title="data.item.severity"
          />
        </template>

        <template #cell(actor)="data">
          <b-media vertical-align="center" no-body>
            <b-media-aside class="mr-50">
              <b-avatar
                size="2rem"
                :variant="actorVariant(data.item.actor.type)"
                :text="initials(data.item.actor.name)"
              />
            </b-media-aside>
            <b-media-body>
              <!-- Snapshot name; links through only when the account still exists. -->
              <b-link
                v-if="data.item.actor.user_id"
                :to="{ name: 'admin-user-single', params: { id: data.item.actor.user_id } }"
                class="font-weight-bold d-block text-truncate actor-name"
                @click.stop
              >
                {{ data.item.actor.name || 'Unknown' }}
              </b-link>
              <span v-else class="font-weight-bold d-block text-truncate actor-name">
                {{ data.item.actor.name || 'Unknown' }}
              </span>
              <small class="text-muted">{{ data.item.actor.type }}</small>
            </b-media-body>
          </b-media>
        </template>

        <template #cell(message)="data">
          <div class="text-truncate message-cell">{{ data.item.message }}</div>
          <div>
            <b-badge pill variant="light-secondary" class="mr-25 small-badge">
              {{ data.item.module_label }}
            </b-badge>
            <b-badge pill variant="light-primary" class="mr-25 small-badge">
              {{ data.item.action_label }}
            </b-badge>
            <!--
              The changed-field count is the single most useful thing in a list row: it is how
              someone spots the edit they are looking for without opening every entry.
            -->
            <b-badge
              v-if="data.item.changes && data.item.changes.length"
              pill
              variant="light-info"
              class="small-badge"
            >
              {{ data.item.changes.length }} field{{ data.item.changes.length === 1 ? '' : 's' }}
            </b-badge>
          </div>
        </template>

        <template #cell(target)="data">
          <span v-if="getValueFromSource(data, 'item.target.label')" class="text-truncate d-block target-cell">
            {{ getValueFromSource(data, 'item.target.label') }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell(outcome)="data">
          <b-badge pill :variant="data.item.outcome_variant">{{ data.item.outcome }}</b-badge>
        </template>

        <template #cell(client)="data">
          <div class="small">{{ getValueFromSource(data, 'item.client.ip_address') || '—' }}</div>
          <small class="text-muted">{{ getValueFromSource(data, 'item.client.device_type') || '' }}</small>
        </template>

        <template #cell(timestamp)="data">
          <div class="small text-nowrap">{{ safeDate(data.item.timestamp) }}</div>
          <small class="text-muted text-nowrap">{{ relative(data.item.timestamp) }}</small>
        </template>
      </b-table>

      <b-card-body v-if="totalRecords > perPage" class="pt-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          align="center"
          class="mb-0"
          first-number
          last-number
        />
      </b-card-body>
    </b-card>

    <activity-log-detail
      :visible.sync="detailVisible"
      :entry="selectedEntry"
      @view-trace="viewTrace"
      @view-history="viewHistory"
    />
  </section>
</template>

<script>
import {
  BCard, BCardBody, BTable, BBadge, BAvatar, BLink, BButton, BSpinner,
  BPagination, BFormSelect, BMedia, BMediaAside, BMediaBody, VBTooltip,
} from 'bootstrap-vue'

import { get, debounce, omitBy } from 'lodash'
import FileDownload from 'js-file-download'
import { formatDate, formatDateDistance } from '@core/utils/utils'

import ActivityLogFilter from '@core/components/shared/logs/ActivityLogFilter.vue'
import ActivityLogDetail from '@core/components/shared/logs/ActivityLogDetail.vue'
import ActivityLogSummary from '@core/components/shared/logs/ActivityLogSummary.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

/** The filter shape, and the reset target. One definition so the two cannot disagree. */
const EMPTY_FILTERS = {
  search: '',
  module: '',
  action: '',
  category: '',
  severity: '',
  outcome: '',
  actor_type: '',
  min_severity: '',
  user_id: '',
  actor_name: '',
  target_collection: '',
  target_id: '',
  ip_address: '',
  correlation_id: '',
  has_changes: '',
  changed_field: '',
  method: '',
  status_code: '',
  from: '',
  to: '',
  period: '7d',
};

export default {
  name: 'ActivityLogs',
  directives: { 'b-tooltip': VBTooltip },
  components: {
    BCard,
    BCardBody,
    BTable,
    BBadge,
    BAvatar,
    BLink,
    BButton,
    BSpinner,
    BPagination,
    BFormSelect,
    BMedia,
    BMediaAside,
    BMediaBody,
    ActivityLogFilter,
    ActivityLogDetail,
    ActivityLogSummary,
  },
  props: {
    /**
     * Filters forced on and hidden from the UI, so this page can serve a narrower screen.
     *
     * The User Logs and Audit Trail pages are this component with a scope applied — one log
     * experience rather than three divergent tables over the same collection.
     */
    scope: {
      type: Object,
      default: () => ({}),
    },
    /** Filter controls to hide, because `scope` already fixes them. */
    lockedFields: {
      type: Array,
      default: () => [],
    },
    /** Heading above the table. */
    title: {
      type: String,
      default: 'Activity',
    },
    /** Replaces the default saved views when a scoped screen needs different ones. */
    savedViews: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      entries: [],
      totalRecords: 0,
      currentPage: 1,
      perPage: 25,
      perPageOptions: [10, 25, 50, 100],

      filters: { ...EMPTY_FILTERS },
      options: {},
      facets: {},
      stats: {},

      detailVisible: false,
      selectedEntry: null,

      debouncedFetch: null,

      tableFields: [
        { key: 'severity', label: '', class: 'severity-column' },
        { key: 'actor', label: 'Who' },
        { key: 'message', label: 'What happened', class: 'message-column' },
        { key: 'target', label: 'Record' },
        { key: 'outcome', label: 'Outcome' },
        { key: 'client', label: 'Origin' },
        { key: 'timestamp', label: 'When' },
      ],
    };
  },
  computed: {
    /**
     * The payload sent to every endpoint. Empty values are dropped so the URL stays readable.
     *
     * `scope` is spread last so it cannot be overridden — not by a saved view, not by a stale URL,
     * not by a user editing the query string. A scoped screen that could be widened by hand would
     * silently become a different screen than its title claims.
     */
    queryParams() {
      return omitBy(
        { ...this.filters, ...this.scope, page: this.currentPage, limit: this.perPage },
        (value) => value === '' || value === null || value === undefined
      );
    },
    activeFilterSummary() {
      const parts = [];
      if (this.filters.search) parts.push(`matching "${this.filters.search}"`);
      if (this.filters.module) parts.push(`${this.filters.module.split(',').length} module(s)`);
      if (this.filters.outcome) parts.push(this.filters.outcome);
      if (this.filters.period) parts.push(`last ${this.filters.period}`);
      return parts.join(' · ');
    },
  },
  watch: {
    /**
     * Page changes refetch directly; filter changes reset to page one first.
     *
     * Without the reset, narrowing a filter while on page 7 shows an empty table and reads as "no
     * results" when the results are simply on page 1.
     */
    currentPage() {
      this.fetchAll();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchAll();
    },
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.debouncedFetch();
      },
    },
  },
  created() {
    // Debounced because typing in the search box mutates `filters` on every keystroke.
    this.debouncedFetch = debounce(this.fetchAll, 350);

    this.hydrateFromRoute();
    this.fetchFilterOptions();
    this.fetchAll();
  },
  methods: {
    /**
     * Date formatting that cannot take the page down.
     *
     * `formatDate` throws `RangeError: Invalid time value` on a missing or unparseable date, and in
     * a `<b-table>` cell that aborts the whole render — one unstamped row blanks the entire log.
     * A log viewer is the screen people open *because* something is wrong, so it has to survive
     * imperfect rows and show what it does have.
     */
    safeDate(value, pattern = 'dd MMM, HH:mm:ss') {
      if (!value) return '—';
      try {
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '—';
        return formatDate(parsed, pattern);
      } catch {
        return '—';
      }
    },
    formatNumber(value) {
      return new Intl.NumberFormat().format(value || 0);
    },
    relative(date) {
      if (!date) return '';
      try {
        const parsed = new Date(date);
        if (Number.isNaN(parsed.getTime())) return '';
        return formatDateDistance(parsed);
      } catch {
        return '';
      }
    },

    /**
     * Restores filters from the URL.
     *
     * The filter state lives in the query string so a view can be linked, bookmarked and reloaded.
     * On an audit screen that is not a convenience: "look at this" between two people has to mean
     * the same rows for both of them.
     */
    hydrateFromRoute() {
      const query = this.$route.query || {};

      Object.keys(EMPTY_FILTERS).forEach((key) => {
        // A locked field is owned by `scope`; restoring it from the URL would let a stale link
        // appear to change a filter the screen does not actually honour.
        if (this.lockedFields.includes(key)) return;
        if (query[key] !== undefined) this.filters[key] = query[key];
      });

      if (query.page) this.currentPage = parseInt(query.page, 10) || 1;
      if (query.limit) this.perPage = parseInt(query.limit, 10) || 25;
    },

    syncRoute() {
      this.$router.replace({ query: this.queryParams }).catch(() => {});
    },

    onFilterChange({ field, value }) {
      // An explicit date range and a relative period are mutually exclusive; keeping both would
      // leave the UI showing a highlighted "7 days" button over a range the server ignored.
      if ((field === 'from' || field === 'to') && value) {
        this.filters.period = '';
      }
      if (field === 'period' && value) {
        this.filters.from = '';
        this.filters.to = '';
      }

      this.filters = { ...this.filters, [field]: value === null ? '' : value };
    },

    /** Applies a saved view on top of a cleared filter, so no stale narrowing survives. */
    applyView(view) {
      this.filters = { ...EMPTY_FILTERS, period: '', ...view };
    },

    resetFilters() {
      this.filters = { ...EMPTY_FILTERS };
    },

    async fetchFilterOptions() {
      try {
        const { data } = await this.useJwt().mainService.activityLogs.filterOptions({
          period: this.filters.period || '30d',
          from: this.filters.from,
          to: this.filters.to,
        });

        this.options = get(data, 'data.options', {});
        this.facets = get(data, 'data.facets', {});
      } catch (error) {
        // The filter bar still works from the taxonomy alone, so a facet failure is not worth
        // interrupting the page for — it only costs the counts beside each option.
        console.warn('Could not load activity filter options', error);
      }
    },

    async fetchAll() {
      this.syncRoute();

      // Both requests share one filter, so the table and the strip can never disagree about
      // which rows they describe.
      await Promise.all([this.fetchEntries(), this.fetchStats()]);
    },

    async fetchEntries() {
      try {
        this.loading = true;

        const { data } = await this.useJwt().mainService.activityLogs.list(this.queryParams);

        this.entries = get(data, 'data', []);
        this.totalRecords = get(data, 'pagination.totalRecords', 0);
      } catch (error) {
        this.notifyError(error, 'Could not load activity');
      } finally {
        this.loading = false;
      }
    },

    async fetchStats() {
      try {
        const { page, limit, ...filterOnly } = this.queryParams;
        const { data } = await this.useJwt().mainService.activityLogs.stats(filterOnly);
        this.stats = get(data, 'data', {});
      } catch (error) {
        console.warn('Could not load activity stats', error);
      }
    },

    /**
     * Opens the detail drawer.
     *
     * The list row is shown immediately and the full entry fetched behind it. List rows already
     * carry everything except the heaviest fields, so the drawer is never blank while loading.
     */
    async openDetail(item) {
      this.selectedEntry = item;
      this.detailVisible = true;

      try {
        const { data } = await this.useJwt().mainService.activityLogs.single(item._id);
        this.selectedEntry = get(data, 'data', item);
      } catch (error) {
        console.warn('Could not load activity detail', error);
      }
    },

    /** Follows a correlation id — every entry from one logical operation. */
    viewTrace(correlationId) {
      this.detailVisible = false;
      this.applyView({ correlation_id: correlationId });
    },

    /** Follows a record — its full history, regardless of who touched it or when. */
    viewHistory(target) {
      this.detailVisible = false;
      this.applyView({
        target_collection: get(target, 'collection', ''),
        target_id: get(target, 'id', ''),
      });
    },

    async onExport() {
      try {
        const result = await this.$swal({
          title: 'Export activity log?',
          text: 'The export uses the filters currently applied.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Export',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) return;

        this.loading = true;

        const { page, limit, ...filterOnly } = this.queryParams;
        const response = await this.useJwt().mainService.activityLogs.export(filterOnly);

        FileDownload(response.data, `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`);

        /**
         * The API caps an export and reports the cap in a header rather than silently truncating.
         * Surfacing it matters: a partial export mistaken for a complete one is how someone draws
         * a confident conclusion from missing evidence.
         */
        const truncated = get(response, 'headers.x-export-truncated');
        if (truncated) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Export truncated',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
              text: `Exported ${truncated} entries. Narrow the date range to export the rest.`,
            },
          });
        }
      } catch (error) {
        this.notifyError(error, 'Export failed — there may be no data for these filters');
      } finally {
        this.loading = false;
      }
    },

    severityColor(severity) {
      return {
        debug: 'secondary',
        info: 'info',
        notice: 'primary',
        warning: 'warning',
        error: 'danger',
        critical: 'danger',
      }[severity] || 'secondary';
    },

    actorVariant(type) {
      return {
        user: 'light-primary',
        anonymous: 'light-secondary',
        api_client: 'light-info',
        system: 'light-warning',
      }[type] || 'light-secondary';
    },

    initials(name) {
      return String(name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');
    },

    notifyError(error, fallback) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          text: get(error, 'response.data.message') || get(error, 'message') || fallback,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.activity-table {
  tbody tr {
    cursor: pointer;
  }

  .severity-column {
    width: 6px;
    padding-left: 0;
    padding-right: 0;
  }

  .message-column {
    min-width: 320px;
    max-width: 480px;
  }

  .actor-name,
  .target-cell {
    max-width: 180px;
  }

  .message-cell {
    max-width: 460px;
  }

  .small-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
}

// A 4px full-height bar instead of a badge: severity stays scannable down the whole table
// without taking a column's worth of width from the message.
.severity-pip {
  width: 4px;
  height: 2.6rem;
  border-radius: 2px;
}

.spin {
  animation: activity-spin 0.8s linear infinite;
}

@keyframes activity-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
